import { hexToRgb, getLuminance, safe } from '../utils/contrast.js';
import { clasesImagenRecorte, modoImagen } from '../utils/imagen-recorte.js';

// ── STRIP DE LOGOS / CATEGORÍAS (carrusel v1 · grid v2) ─────────
// Núcleo compartido por los bloques "logos" (marcas) y "catalogo" (categorías):
// misma estructura visual (imagen + nombre + enlace a su página de tienda) y mismo
// modelo de datos (los items viven en props.logos = [{name,image,url}] en AMBOS,
// para que el extractor y el render se reusen sin duplicar). opts parametriza solo
// el id de sección y los defaults (título + items de ejemplo si se inserta vacío).

// ── TAMAÑO GRADUABLE (edición 100% del cliente, la IA no lo genera) ──
// DOS MODOS por bloque, elegidos en el editor (props.size_mode a NIVEL DE BLOQUE):
//   · 'manual' (default / ausente): cada marca/categoría su propio tamaño. SSOT =
//     props.logos[i].size, entero 0..LOGO_SIZE_MAX controlado por el stepper − / + de cada píldora.
//   · 'uniform' (Predefinido): UN tamaño global que aplican TODAS las marcas a la vez.
//     SSOT = props.uniform_size (0..LOGO_SIZE_MAX), elegido con el selector único del editor. Los
//     props.logos[i].size se CONSERVAN (no se borran) → volver a Manual los restaura.
// Escala (idéntica en ambos modos): 0=Normal(56px) · 1=Grande(80) · 2=Enorme(110) · 3=Gigante(150)
// · 4=Máximo(200). La altura es la de la CAJA de la imagen; con recuadro, su relleno sale de ahí.
// El nivel gradúa las dimensiones de la imagen (y del nombre) vía las clases .logo-size-N
// de css/site.css. effectiveSize(p,l) resuelve el nivel según el modo → Uniform y Manual
// comparten el MISMO render (buildDisplayItem) sin duplicar.
// Retrocompat: los sitios guardados antes usaban el flag booleano `featured`; si no hay
// `size`, se mapea featured:true → nivel 1 ("Grande"). size_mode ausente ⇒ Manual intacto.
//
// ── RECUADRO (props.frame, a NIVEL DE BLOQUE) ──
// Desde el nivel Grande, cada imagen va dentro de un recuadro (relleno + fondo y anillo del
// acento: `.logo-item.logo-featured img` en site.css). `frame: 'none'` lo quita (clase
// `.logos-sin-marco` en la sección) y la imagen ocupa TODA la altura del nivel. Ausente =
// con recuadro, como siempre. Lo cambia el interruptor de la barra (.logo-frame-btn, tools.js).
//
// ── NOMBRE (props.names, a NIVEL DE BLOQUE) ──
// Cada logo lleva su nombre debajo (`.logo-name`). `names: 'none'` los quita TODOS (clase
// `.logos-sin-nombre` en la sección; css/site.css los esconde) y queda solo el logo: el nombre
// sigue en el `alt` de la imagen y en el `title` del enlace. Ausente = con nombre, como siempre.
// Lo cambia el interruptor de la barra (.logo-names-btn, tools.js). Pedido del dueño, 01-10-2026.
const LOGO_SIZE_LABELS = ['Normal', 'Grande', 'Enorme', 'Gigante', 'Máximo'];
export const LOGO_SIZE_MAX = LOGO_SIZE_LABELS.length - 1;
// Normaliza cualquier valor a un nivel entero 0..LOGO_SIZE_MAX (SSOT de la escala). Compartido
// por el nivel per-item (Manual), el de bloque (uniform_size) y los botones de editor/tools.js.
export const clampSize = (n) => {
    const r = Math.round(Number(n));
    return Math.max(0, Math.min(LOGO_SIZE_MAX, Number.isNaN(r) ? 0 : r));
};
// Nivel efectivo PER-ITEM (modo Manual): sale de l.size, con retrocompat featured→1.
const logoSizeOf = (l) => clampSize((l && l.size != null) ? l.size : (l && l.featured ? 1 : 0));
// Nivel efectivo del ITEM al pintar, según el MODO del bloque (p.size_mode):
//   · 'uniform' → todas las marcas usan p.uniform_size (un solo tamaño global).
//   · 'manual'/ausente → cada item su propio l.size (comportamiento retrocompatible).
const effectiveSize = (p, l) => p.size_mode === 'uniform' ? clampSize(p.uniform_size) : logoSizeOf(l);

const mediaStripBlock = (rawProps, opts) => {
    const p = safe(rawProps);
    const variant = p.variant || 'v1';
    const title = p.title || opts.defaultTitle;
    // SIN elementos la tira NO se publica (utils/publicable.js: render.js y compileSiteHtml ni la
    // pintan). En el editor enseña sus ejemplos de VISTA PREVIA, pero sin píldoras editables: el
    // extractor reconstruye `props.logos` desde ellas (`.logo-edit-item`), y el primer Guardar
    // convertía los ejemplos en datos reales y publicados (17-10-2026: «Empresas que confían en
    // nosotros» con Google, Microsoft, Apple…).
    const vacia = !(Array.isArray(p.logos) && p.logos.length > 0);
    const logos = vacia ? opts.defaultItems : p.logos;

    const placeholder = "https://placehold.co/150x50/cccccc/666666?text=";

    // Item de presentación (carrusel v1 y grid v2). Si el item tiene `url` (su
    // página en la tienda: /slug-b-id/ marca o /slug-c1-id/ categoría) se envuelve
    // en <a class="logo-link">. En el editor un click NO navega (guard en
    // marcas-panel.js); en el sitio publicado lleva a la tienda.
    const buildDisplayItem = (l, extraStyle = '') => {
        const cleanName = l.name.replace(/<[^>]*>?/gm, '').trim();
        const safeName = cleanName.replace(/"/g, '&quot;');
        // Tamaño graduable (edición 100% del cliente, la IA no lo genera): el nivel
        // efectivo (0..3) lo resuelve effectiveSize según el MODO del bloque — en Uniform
        // TODAS las marcas al p.uniform_size, en Manual cada una su props.logos[i].size (o
        // retrocompat con featured). Un nivel > 0 añade .logo-featured (base de realce con
        // el acento de marca) + .logo-size-N (dimensiones de la imagen), vía css/site.css.
        // Este render NO depende de editorMode ni pinta chrome de modo, así que las clases
        // viajan al HTML compilado por compileSiteHtml → tamaño visible en el publicado.
        const size = effectiveSize(p, l);
        const featCls = size > 0 ? ` logo-featured logo-size-${size}` : '';
        // Estilo «Recorte» POR LOGO (utils/imagen-recorte.js, D-1452: logos PNG transparentes): sin
        // el recuadro de los niveles grandes y, si se pide, con la sombra de la silueta. Sin prop, nada.
        const recorte = clasesImagenRecorte(l).trim();
        const inner = `
            <img src="${l.image || placeholder + encodeURIComponent(cleanName)}" alt="${safeName}"${recorte ? ` class="${recorte}"` : ''} loading="lazy">
            <span class="logo-name">${l.name}</span>`;
        return l.url
            ? `<a class="logo-item logo-link${featCls}" href="${l.url}" title="${safeName}" style="${extraStyle}">${inner}</a>`
            : `<div class="logo-item${featCls}" style="${extraStyle}">${inner}</div>`;
    };

    // «Estilo de la imagen» de UN logo (D-1452): abre el panel de Tarjeta/Recorte sobre este item
    // (editor/imagen-estilo.js, despacho en editor/tools.js). Solo con imagen propia. Encendido
    // (blanco sobre un fondo tenue: la píldora es oscura, el acento del sitio podría no leerse) si
    // el logo va en recorte. Va en la píldora, que es `.editor-only`: no se publica.
    const botonRecorte = (l, i) => {
        const on = modoImagen(l).recorte;
        return `<button class="tool-btn logo-estilo-btn" data-array="logos" data-index="${i}" aria-pressed="${on ? 'true' : 'false'}" title="Estilo de la imagen (Recorte, para PNG con el fondo transparente)" aria-label="Estilo de la imagen" style="width:20px;height:20px;padding:2px;background:${on ? 'rgba(255,255,255,0.16)' : 'transparent'};border:none;border-radius:6px;color:${on ? '#fff' : 'rgba(255,255,255,0.5)'};cursor:pointer;"><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 2v14a2 2 0 0 0 2 2h14"/><path d="M18 22V8a2 2 0 0 0-2-2H2"/></svg></button>`;
    };

    // --- Editor items (compartidos). data-array/prop = "logos" en ambos tipos. ---
    const buildEditItem = (l, i) => {
        const cleanName = l.name.replace(/<[^>]*>?/gm, '').trim();
        // Mismo saneo que buildDisplayItem: el nombre lo edita el cliente inline.
        const safeEditName = cleanName.replace(/"/g, '&quot;');
        // Stepper de TAMAÑO (− / +) SOLO en modo Manual: el cliente gradúa el nivel 0..3
        // de cada marca/categoría (dispatch en editor/tools.js → escribe props.logos[i].size).
        // Se atenúa − en nivel 0 y + en LOGO_SIZE_MAX (topes). En modo Predefinido (uniform) el
        // tamaño lo rige el selector global de la cabecera, así que la píldora oculta el
        // stepper (evita un control per-item que no tendría efecto); l.size se conserva.
        const size = logoSizeOf(l);
        const stepBtn = 'width:20px;height:20px;padding:0;background:transparent;border:none;color:#fff;font-size:1.05rem;font-weight:700;line-height:1;cursor:pointer;transition:0.2s;flex-shrink:0;';
        const stepper = p.size_mode === 'uniform' ? '' : `
            <span class="logo-size-stepper" title="Tamaño del elemento" style="display:inline-flex;align-items:center;gap:1px;background:rgba(255,255,255,0.07);border-radius:var(--card-radius, 20px);padding:1px 3px;flex-shrink:0;">
                <button class="tool-btn logo-size-dec" data-array="logos" data-index="${i}" title="Reducir tamaño" style="${stepBtn}opacity:${size <= 0 ? '0.3' : '0.8'};">&minus;</button>
                <span class="logo-size-level" style="font-size:0.62rem;font-weight:700;min-width:46px;text-align:center;color:rgba(255,255,255,0.8);user-select:none;">${LOGO_SIZE_LABELS[size]}</span>
                <button class="tool-btn logo-size-inc" data-array="logos" data-index="${i}" title="Aumentar tamaño" style="${stepBtn}opacity:${size >= LOGO_SIZE_MAX ? '0.3' : '0.8'};">+</button>
            </span>`;
        return `
        <div class="logo-edit-item sortable-item" data-item-index="${i}" data-array-name="logos" style="position:relative;display:inline-flex;align-items:center;gap:8px;background:rgba(15,23,42,0.92);border:1px solid rgba(255,255,255,0.14);border-radius:var(--card-radius, 30px);padding:8px 16px;box-shadow:0 2px 10px rgba(0,0,0,0.28);transition:border-color 0.2s;">
            <img src="${l.image || placeholder + encodeURIComponent(cleanName)}" alt="${safeEditName}" style="height:18px;width:auto;border-radius:4px;">
            <span contenteditable="true" data-field="name" data-index="${i}" style="font-size:0.8rem;font-weight:700;outline:none;cursor:text;min-width:40px;color:#e2e8f0;">${l.name}</span>
            <button class="tool-btn image-picker-btn" data-array="logos" data-index="${i}" title="Cambiar imagen" style="width:20px;height:20px;padding:2px;background:transparent;border:none;color:rgba(255,255,255,0.5);cursor:pointer;margin-left:4px;">${window.icons.image}</button>
            ${l.image ? botonRecorte(l, i) : ''}
            ${stepper}
            <button class="item-delete-btn" data-array="logos" data-index="${i}" title="Eliminar" style="background:rgba(255,255,255,0.08);border:none;color:rgba(255,255,255,0.5);cursor:pointer;font-size:0.7rem;padding:0;border-radius:50%;width:22px;height:22px;display:flex;align-items:center;justify-content:center;transition:0.2s;flex-shrink:0;">✕</button>
        </div>`;
    };

    const editItems = vacia
        ? `<div class="logos-aviso-vacio" role="note" style="display:inline-flex;align-items:center;gap:8px;background:rgba(15,23,42,0.92);border:1px dashed rgba(255,255,255,0.35);border-radius:var(--card-radius, 30px);padding:8px 16px;color:#e2e8f0;font-size:0.8rem;font-weight:600;">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="11" x2="12" y2="16"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
            <span>${opts.avisoVacio}</span>
        </div>`
        : logos.map((l, i) => buildEditItem(l, i)).join('');

    // Cabecera de MODO de tamaño (editor-only, encima de las píldoras): control segmentado
    // [ Predefinido | Manual ] (.logo-mode-btn). En Predefinido (uniform) se pinta debajo un
    // selector único de tamaño (.logo-usize-btn) que rige TODAS las marcas (props.uniform_size,
    // etiquetas LOGO_SIZE_LABELS). Los data-* los despacha editor/tools.js. Todo .editor-only →
    // no viaja al publicado (compileSiteHtml elimina .editor-only). Chrome en css/site.css.
    const mode = p.size_mode === 'uniform' ? 'uniform' : 'manual';
    const usize = clampSize(p.uniform_size);
    const sinMarco = p.frame === 'none';
    const sinNombre = p.names === 'none';
    const clasesBloque = (sinMarco ? ' logos-sin-marco' : '') + (sinNombre ? ' logos-sin-nombre' : '');
    const modeHeader = `
        <div class="logos-size-toolbar editor-only">
            <div class="logos-toolbar-fila">
            <div class="logo-mode-switch" role="group" aria-label="Modo de tamaño">
                <button class="logo-mode-btn${mode === 'uniform' ? ' is-active' : ''}" data-mode="uniform" title="Un tamaño único para todas las marcas">Predefinido</button>
                <button class="logo-mode-btn${mode === 'manual' ? ' is-active' : ''}" data-mode="manual" title="Tamaño por marca (control en cada píldora)">Manual</button>
            </div>
            <div class="logo-mode-switch" role="group" aria-label="Recuadro de cada elemento">
                <button class="logo-frame-btn${sinMarco ? '' : ' is-active'}" data-frame="box" title="Desde el tamaño Grande, cada imagen va dentro de un recuadro">Con recuadro</button>
                <button class="logo-frame-btn${sinMarco ? ' is-active' : ''}" data-frame="none" title="Solo la imagen, sin borde ni fondo, a toda la altura del tamaño">Sin recuadro</button>
            </div>
            <div class="logo-mode-switch" role="group" aria-label="Nombre de cada elemento">
                <button class="logo-names-btn${sinNombre ? '' : ' is-active'}" data-names="show" title="El nombre debajo de cada logo">Con nombre</button>
                <button class="logo-names-btn${sinNombre ? ' is-active' : ''}" data-names="none" title="Solo el logo, sin el nombre debajo">Sin nombre</button>
            </div>
            </div>
            ${mode === 'uniform' ? `
            <div class="logo-usize-row" role="group" aria-label="Tamaño de todas las marcas">
                ${LOGO_SIZE_LABELS.map((lbl, n) => `<button class="logo-usize-btn${usize === n ? ' is-active' : ''}" data-usize="${n}">${lbl}</button>`).join('')}
            </div>` : ''}
        </div>`;

    const editList = `
        ${modeHeader}
        <div class="logos-edit-list editor-only" style="display:flex;flex-wrap:wrap;gap:10px;justify-content:center;padding:0 8%;margin-bottom:24px;align-items:center;">
            ${editItems}
            <button class="item-add-btn" data-array="logos" data-index="${vacia ? 0 : logos.length - 1}" style="width:34px;height:34px;border-radius:50%;background:var(--accent);color:#fff;border:none;font-size:1.2rem;font-weight:900;cursor:pointer;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 12px color-mix(in srgb,var(--accent) 40%,transparent);transition:0.2s;flex-shrink:0;" title="Añadir elemento">+</button>
        </div>`;

    // El antetítulo (`title`) va a 1 rem, sin velo y con 0,08 em de respaldo (01-10-2026): a 0,9 rem,
    // opacidad 0,6, 3 px de tracking y las versalitas de la capa de rótulo de site.css, «Nuestro
    // catálogo» se quedaba en 9 px de letra y 4:1 de contraste («apenas se ve», el dueño).
    // --- v1 (carrusel infinito) ---
    if (variant === 'v1' || (!variant || variant === '')) {
        const gap = Math.max(48, 110 - (logos.length * 4));
        const trackItems = logos.map((l) => buildDisplayItem(l, `padding: 0 ${gap / 2}px;`)).join('');
        const repeticiones = Math.max(2, Math.ceil(12 / logos.length) * 2);
        const halfTrack = new Array(repeticiones / 2).fill(trackItems).join('');

        return `
    <section id="bloque-${opts.sectionId}" class="reveal variant-${variant}${clasesBloque}" style="--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --font:'${p.fontFamily || "Inter"}'; padding:80px 0;">
        <p data-field="title" contenteditable="true" style="text-align:center; font-size:calc(1rem * var(--type-scale, 1)); font-weight:800; letter-spacing:var(--kicker-tracking, 0.08em); text-transform:var(--kicker-case, uppercase); margin-bottom:32px;">${title}</p>
        ${editList}
        <div class="logos-track-outer"><div class="logos-track">${halfTrack}${halfTrack}</div></div>
    </section>`;
    }

    // --- v2 (grid estático) ---
    return `
    <section id="bloque-${opts.sectionId}" class="reveal variant-${variant}${clasesBloque}" style="--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --font:'${p.fontFamily || "Inter"}'; padding:80px 8%;">
        <p data-field="title" contenteditable="true" style="text-align:center; font-size:calc(1rem * var(--type-scale, 1)); font-weight:800; letter-spacing:var(--kicker-tracking, 0.08em); text-transform:var(--kicker-case, uppercase); margin-bottom:48px;">${title}</p>
        ${editList}
        <div class="logos-grid" style="display: flex; flex-wrap: wrap; justify-content: center; align-items: center; gap: 48px 40px; max-width: 1000px; margin: 0 auto;">
            ${logos.map((l) => buildDisplayItem(l)).join('')}
        </div>
    </section>`;
};

// Ejemplos de VISTA PREVIA: solo se ven en el editor con la tira vacía (sin elementos no se publica
// y no se guardan, ver `vacia`). NEUTROS a propósito: eran Google, Microsoft, Apple, Meta…, marcas
// reales que el cliente no tiene por qué tener de cliente. `elementoNuevo` es lo que inserta el «+»
// de una tira vacía (editor/tools.js: no hay elemento que clonar).
const BRAND_DEFAULTS = {
    sectionId: 'logos',
    defaultTitle: 'Empresas que confían en nosotros',
    defaultItems: [1, 2, 3, 4, 5, 6].map((n) => ({ name: 'Logo ' + n, image: '' })),
    avisoVacio: 'Sin logos: esta sección no se publica hasta que añadas al menos uno con «+».',
    elementoNuevo: { name: 'Nuevo logo', image: '' },
};

const CATEGORY_DEFAULTS = {
    sectionId: 'catalogo',
    defaultTitle: 'Nuestro catálogo',
    defaultItems: [1, 2, 3, 4, 5, 6].map((n) => ({ name: 'Categoría ' + n, image: '' })),
    avisoVacio: 'Sin categorías: esta sección no se publica hasta que añadas al menos una con «+».',
    elementoNuevo: { name: 'Nueva categoría', image: '' },
};

/** Elemento que añade el «+» a una tira VACÍA de ese tipo (null si el tipo no es una tira). */
export function elementoNuevoDeTira(tipo) {
    const cfg = tipo === 'logos' ? BRAND_DEFAULTS : tipo === 'catalogo' ? CATEGORY_DEFAULTS : null;
    return cfg ? Object.assign({}, cfg.elementoNuevo) : null;
}

// ── Bloque MARCAS (logos) ──
export const logosBlock = (rawProps) => mediaStripBlock(rawProps, BRAND_DEFAULTS);

// ── Bloque CATÁLOGO (categorías) — reusa el mismo render ──
export const catalogoBlock = (rawProps) => mediaStripBlock(rawProps, CATEGORY_DEFAULTS);
